import React, {JSX, useMemo, useState} from 'react';
import { FieldArray, useField } from "formik";
import Button from "react-bootstrap/Button";
import {Trash3} from "react-bootstrap-icons";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import cx from "classnames";
import {useSorting} from "fearless-common";


function DeleteButton({ onDelete, onSuccess }: { onDelete: () => Promise<void>; onSuccess: () => void; }) {
    const [isDeleting, setDeleting] = useState(false);
    const [error, setError] = useState('');

    return (
        <>
            <Button
                variant="outline-danger"
                className="border-0"
                onClick={() => {
                    setError('')
                    if (!isDeleting) {
                        if (typeof onDelete === 'function') {
                            setDeleting(true)
                            onDelete()
                                .then(() => {
                                    onSuccess()
                                })
                                .catch((e) => {
                                    setError(e.response?.data?.message)
                                })
                                .finally(() => setDeleting(false))
                        } else {
                            onSuccess()
                        }
                    }
                }}
            >
                <Trash3 />
            </Button>
            {error && <Alert className="alert-danger">{error}</Alert>}
        </>
    )
}

export default function DynamicList<T extends Record<string, any>>({ name, Component, onDelete, headers }: {
  Component: React.FunctionComponent<{ name: string; item: T }>;
  name: string;
  onDelete: (item: T) => Promise<void>;
  headers: { label: JSX.Element; size: number; className?: string, sortable?: string; labelAfterSort?: React.JSX.Element | null }[],
}) {
    const [{ value }] = useField<T[]>(name);
    const { sortKey, sortFunc, setSortKey, toggleDirection, direction } = useSorting(headers.find(h => h.sortable)?.sortable || '');

    const sortedValue = useMemo(() => {
      value.sort(sortFunc);
      return value;
    }, [value, sortFunc, sortKey]);

    return (
      <>
        <Row className="py-2 fw-bold">{headers.map(({ label, labelAfterSort, size, className, sortable }, idx) => (
          <Col key={idx} sm={size} className={className}>
            {label}
            {sortable && (
              <button
                onClick={sortKey === sortable ? toggleDirection: () => setSortKey(sortable)}
                type="button"
                className={cx({
                  'border-0 bg-transparent': true,
                  'active-color': sortKey === sortable,
                })}
              >
                <i className={sortKey === sortable && direction === 1 ? 'fa fa-arrow-up': 'fa fa-arrow-down'} />
              </button>
            )}
            {labelAfterSort}
          </Col>
        ))}</Row>
        <FieldArray
          name={name}
          render={arrayHelpers => {
            const hasItems = sortedValue && sortedValue.length > 0;
            return (
              <div>
                {hasItems && (
                  sortedValue.map((item, index) => (
                    <Row
                      key={index}
                      className={cx({
                        "d-sm-flex flex-row py-2 align-items-center": true,
                        "border-bottom border-default": true,
                        "border-top": index === 0,
                      })}
                    >
                      <Component item={item} name={`${name}.${index}`} />
                      {sortedValue.length >= 2 && (
                        <Col className="text-end">
                          <DeleteButton
                            onSuccess={() => {
                              arrayHelpers.remove(index)
                            }}
                            onDelete={() => onDelete(item)}
                          />
                        </Col>
                      )}
                    </Row>
                  ))
                )}
              </div>
            )
          }}
        />
      </>
    );
}
